import apiClient from './axios';
import { ApiResponse } from '../types';

/**
 * Invoices businesses have raised.
 *
 * Read-only by design. An invoice is a document between a business and its
 * customer — PayIQ editing one after the fact is forging a record their
 * accounts depend on. This exists to answer "the customer says they paid"
 * and "why does mine say overdue" without reading the database.
 */

export type InvoiceStatus = 'draft' | 'sent' | 'part_paid' | 'paid' | 'void';

export interface InvoiceItem {
  id: string;
  description: string;
  quantity: string;
  unitPriceKobo: string;
  lineTotalKobo: string;
  position: number;
}

export interface Invoice {
  id: string;
  businessId: string;
  /** Empty on a draft — numbers are assigned at send. */
  number: string;
  customerName: string;
  status: InvoiceStatus;
  issueDate: string;
  dueDate: string;
  subtotalKobo: string;
  vatKobo: string;
  withholdingKobo: string;
  totalKobo: string;
  amountPaidKobo: string;
  balanceKobo: string;
  /** Derived server-side, so this page and the customer's app always agree. */
  overdue: boolean;
  paymentTerms: string | null;
  purchaseOrderRef: string | null;
  notes: string | null;
  sentAt: string | null;
  paidAt: string | null;
  voidReason: string | null;
  remindersSent: number;
  items?: InvoiceItem[];
  createdAt: string;
}

export interface ListInvoicesParams {
  status?: InvoiceStatus;
  businessId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const invoicesApi = {
  list: async (params: ListInvoicesParams = {}) => {
    const { data } = await apiClient.get<
      ApiResponse<{
        items: Invoice[];
        total: number;
        page: number;
        limit: number;
      }>
    >('/admin/invoices', { params });
    return data.data;
  },

  get: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<Invoice>>(
      `/admin/invoices/${id}`,
    );
    return data.data;
  },
};
